import { HttpClient, HttpErrorResponse, HttpEventType, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IDictionary } from '@salesrun/basic-types';
import { BlockingLoaderProgress } from '@salesrun/ngx-blocking-loader';
import { EnvironmentService } from '@shared/environment/environment.service';
import { Observable, of, skipWhile, switchMap } from 'rxjs';
import { appConfig } from '../../app/app.config.mjs';


@Injectable()
export class ApiService {

    constructor(
        private _httpClient: HttpClient,
        private _environment: EnvironmentService,
    ) {
    }

    get<T>(
        url: string,
        params?: HttpParams | IDictionary<string | number | boolean | ReadonlyArray<string | number | boolean>>,
        responseType: any = 'json',
    ): Observable<T> {
        return this._httpClient.get<T>(this.addBaseUrlForRelativeApiUrls(url), { params: params, responseType: responseType });
    }

    post<T>(url: string, params?: unknown, httpParams?: HttpParams): Observable<T> {
        return httpParams
            ? this._httpClient.post<T>(this.addBaseUrlForRelativeApiUrls(url), params, { params: httpParams })
            : this._httpClient.post<T>(this.addBaseUrlForRelativeApiUrls(url), params);
    }

    uploadFile<T>(
        url: string,
        params: unknown,
        onProgress: (progress: BlockingLoaderProgress) => unknown,
        method: 'POST' | 'PUT' = 'POST',
    ): Observable<T> {
        const uploaderApi = url.startsWith('/api')
            ? this._environment.current.uploaderHost
            : this.addBaseUrlForRelativeUrls(this._environment.current.uploaderHost, 'api');
        const requestUrl = this.addBaseUrlForRelativeUrls(uploaderApi, url);
        const request = new HttpRequest(method, requestUrl, params, {
            reportProgress: true,
        });

        return this._httpClient.request<T>(request)
            .pipe(
                switchMap(event => {
                    switch (event.type) {
                        case HttpEventType.UploadProgress: {
                            onProgress(new BlockingLoaderProgress(true, Math.round(100 * event.loaded / event.total)));
                            break;
                        }
                        case HttpEventType.Response: {
                            onProgress(new BlockingLoaderProgress(false, 0));
                            if (event.ok) {
                                return of(event.body);
                            }
                            else {
                                // eslint-disable-next-line @typescript-eslint/only-throw-error
                                throw new HttpErrorResponse(event);
                            }
                        }
                    }
                    return of(null);
                }),
                skipWhile(q => q == null),
            );
    }

    put<T>(url: string, params?: unknown): Observable<T> {
        return this._httpClient.put<T>(this.addBaseUrlForRelativeApiUrls(url), params);
    }

    delete<T>(url: string, params?: HttpParams | IDictionary<string | number | boolean | ReadonlyArray<string | number | boolean>>): Observable<T> {
        return this._httpClient.delete<T>(this.addBaseUrlForRelativeApiUrls(url), { params: params });
    }

    patch<T>(url: string, body?: unknown): Observable<T> {
        return this._httpClient.patch<T>(this.addBaseUrlForRelativeApiUrls(url), body);
    }

    getText(url: string): Observable<string> {
        return this._httpClient.get(this.addBaseUrlForRelativeApiUrls(url), { responseType: 'text' });
    }


    private addBaseUrlForRelativeApiUrls(url: string): string {
        const backendUrl = url.startsWith('/api') ? appConfig.backendUrl : `${appConfig.backendUrl}/api`;
        return this.addBaseUrlForRelativeUrls(backendUrl, url);
    }

    private addBaseUrlForRelativeUrls(baseUrl: string, url: string): string {
        if (/^https?:\/\//i.test(url)) {
            return url;
        }
        return `${baseUrl.replace(/^\/+|\/+$/g, '')}/${url.replace(/^\/+|\/+$/g, '')}`;
    }
}
